import {
  Dialog,
  Divider,
  Stack,
  SwipeableDrawer
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { PickersDayProps } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar, DateCalendarSlotsComponentsProps } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  compareAsc,
  format,
  isSameDay
} from "date-fns";
import it from "date-fns/locale/it";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarArrow,
  CalendarContainerStack,
  CurrentDayOfWeekTypography,
  CurrentDayTypography,
  DotSpan,
  LabelTypography,
  SmartLearningButton,
  StyledPickersDay
} from "./CalendarCard.style";
import { SmartLearningModalContent } from "./SmartLearningModalContent.component";
import { switchLocaleImport } from "../../components/Calendar/LocaleLogic";
import { Icon } from "../../components/Icon";
import { useAuthStore, useLanguageStore } from "../../stores";
import type { CalendarDateObject } from "../../types";
import {
  useDayColor,
  withPickersDayProps
} from "../../utils/calendar";

export function CalendarCard({
  dateObjects = [],
  dateNumbersToHighlight,
  selectedExternal,
  setSelectedExternal,
  setSelectedMonth,
  setSelectedYear
}: {
  dateObjects?: CalendarDateObject[]
  dateNumbersToHighlight: number[]
  selectedExternal: Date
  setSelectedExternal: Dispatch<SetStateAction<Date>>
  setSelectedMonth: Dispatch<SetStateAction<number>>
  setSelectedYear: Dispatch<SetStateAction<number>>
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const smartLearning = useAuthStore(state => state.smartConfiguration?.smartLearning);
  const currentDay = new Date();
  const [selected, setSelected] = useState<Date>(selectedExternal);

  // const [daysToHighlightState, setDaysToHighlightState] = useState<number[]>([]);
  // const [dateObjectsState, setDateObjectsState] = useState<CalendarDateObject[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const language = useLanguageStore(state => state.language)?.id;

  const [locale, setLocale] = useState<Locale>(it);

  useEffect(() => {
    (async () => {
      try {
        const newLocale = await switchLocaleImport(language);
        setLocale(newLocale.default);
      } catch(err) {
        // eslint-disable-next-line no-console
        console.error("import date-fns locale:", err);
      }
    })();
  }, [language]);

  // useEffect(() => {
  //   if(dateObjects) {
  //     setDateObjectsState(dateObjects);
  //   }
  // }, [dateObjects]);

  // useEffect(() => {
  //   if(daysToHighlight) {
  //     setDaysToHighlightState(daysToHighlight);
  //   }
  // }, [daysToHighlight]);

  const stringDay = format(currentDay, "EEEE", { locale: locale });
  const numberDay = format(currentDay, "dd", { locale: locale });

  return (
    <>
      <CalendarContainerStack aria-label={ stringDay + numberDay }>
        <Stack direction="row">
          {
            useMediaQuery(theme.breakpoints.up("sm")) ? (
              <Stack aria-hidden="true" sx={ { color: theme.customColors.exploreCardText } }>
                <CurrentDayOfWeekTypography>
                  { format(currentDay, "EEEE", { locale: locale }) }
                </CurrentDayOfWeekTypography>
                <CurrentDayTypography>
                  { format(currentDay, "dd", { locale: locale }) }
                </CurrentDayTypography>
              </Stack>
            ) : null
          }
          <LocalizationProvider
            dateAdapter={ AdapterDateFns }
            adapterLocale={ locale }>
            <DateCalendar
              disableHighlightToday={ true }
              value={ selected }
              onChange={ (newDate) => {
                newDate?.setHours(0, 0, 0);
                setSelected(newDate ?? new Date());
                setSelectedExternal(newDate ?? new Date());
              } }
              onMonthChange={ (month) => {
                month.setHours(0, 0, 0);
                setSelected(month);
                setSelectedExternal(month);
                setSelectedMonth(parseInt(format(month, "MM")));
                setSelectedYear(parseInt(format(month, "yyyy")));
              } }
              onYearChange={ (year) => {
                year.setHours(0, 0, 0);
                setSelected(year);
                setSelectedExternal(year);
                setSelectedYear(parseInt(format(year, "yyyy")));
              } }
              slots={ {
                day: SeededCalendarCardDay({ dateNumbersToHighlight, dateObjects, setSelected }),
                leftArrowIcon:
                () => <CalendarArrow    icon={ "arrow_left_glow" } iconGlow={ "Icons_arrow_left_glow" } description={ t("calendar_prev_month") } />,
                rightArrowIcon:
                () => <CalendarArrow icon={ "Icons_arrow-right_glow" } iconGlow={ "arrow_right_filled_glow" } description={ t("calendar_next_month") } />
              } }
              slotProps={ {
                nextIconButton: {
                  "aria-label": t("calendar_next_month"),
                  "title": t("calendar_next_month"),
                  sx: {
                    "&.Mui-focusVisible": {
                      outline: `2px solid ${theme.customColors.exploreCardText}`,
                      outlineOffset: "2px",
                      borderRadius: "2px"
                    }
                  }
                },
                previousIconButton: {
                  "aria-label": t("calendar_prev_month"),
                  "title": t("calendar_prev_month"),
                  sx: {
                    "&.Mui-focusVisible": {
                      outline: `2px solid ${theme.customColors.exploreCardText}`,
                      outlineOffset: "2px",
                      borderRadius: "2px"
                    }
                  }
                },
                switchViewButton: {
                  sx: {
                    display: "inline-flex !important",
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    opacity: 1,
                    pointerEvents: "auto"
                  } }
              } as unknown as DateCalendarSlotsComponentsProps<Date> }
              sx={ {
                "& .MuiPickersCalendarHeader-switchViewButton": {
                  tabIndex: 0,
                  "&:focus-visible": {
                    outline: `2px solid ${theme.customColors.exploreCardText}`,
                    outlineOffset: "2px",
                    borderRadius: "2px"
                  }
                }
              } }
              showDaysOutsideCurrentMonth={ false }
            />
          </LocalizationProvider>
        </Stack>
        { smartLearning &&
          <SmartLearningButton
            tabIndex={ 0 }
            id={ "smart_learning_button" }
            onClick={ () => setIsOpen(true) }
            aria-label={ t("smart_learning") }
          >
            <Stack
              aria-hidden="true"
              direction={ "row" }
              divider={
                <Divider
                  aria-hidden="true"
                  orientation="vertical"
                  sx={ {
                    borderColor: theme.customColors.textPrimary,
                    color: theme.customColors.systemPrimary01,
                    height: "auto",
                    margin: "0 16px"
                  } }
                />
              }
            >
              <Icon
                aria-hidden="true"
                color={ theme.customColors.systemPrimary01 }
                icon={ "smart_learning" }
                width={ "25px" }
                height={ "32px" }
              />
              <LabelTypography aria-hidden="true" sx={ { color: theme.customColors.systemPrimary01 } }>
                { t("smart_learning") }
              </LabelTypography>
            </Stack>
          </SmartLearningButton>
        }
        { isMobile ?
          <SwipeableDrawer
            anchor={ "bottom" }
            open={ isOpen }
            onClose={ () => setIsOpen(false) }
            onOpen={ () => setIsOpen(true) }
            className={ "smart-drawer" }
            sx={ {
              "& .MuiModal-backdrop": {
                bottom: "60px",

                [theme.breakpoints.down("sm")]:{
                  height: "calc(100% - 120px)"
                }
              },

              "& .MuiPaper-root": {
                background: theme.customColors.backgroundCard,
                border: `2px solid  ${theme.customColors.border}`, 
                "&:hover": {
                  border: `2px solid ${theme.customColors.borderAccent}`
                },
                borderColor: theme.customColors.borderAccent,
                borderRadius: "8px 8px 0 0",
                bottom: "60px",
                boxShadow: `0 0 4px 0 ${theme.customColors.borderAccent}`,
                padding: "16px 20px",
                transition: "none",

                [theme.breakpoints.down("sm")]:{
                  height: "calc(100% - 120px)"
                }
              }
            } }
          >
            <SmartLearningModalContent
              onClose={ () => setIsOpen(false) }
              selectedDate={ selected }
            />
          </SwipeableDrawer> :
          <Dialog
            maxWidth={ false }
            open={ isOpen }
            onClose={ (event, reason) => {
              if (reason == "backdropClick" || reason == "escapeKeyDown") {
                return;
              }
              setIsOpen(false);
            } }
            scroll={ "paper" }
            sx={ {
              ".MuiDialog-paper ": {
                borderRadius: "4px",
                bottom: "10%",
                //maxHeight:"calc(100% - 32px)",
                padding: "24px 40px",
                width: "494px",
                background: theme.customColors.backgroundCard,
                border: `2px solid  ${theme.customColors.border}`, 
                "&:hover": {
                  border: `2px solid ${theme.customColors.borderAccent}`
                }
              }
            } }
          >
            <SmartLearningModalContent
              onClose={ () => setIsOpen(false) }
              selectedDate={ selected }
            />
          </Dialog> }
      </CalendarContainerStack>
    </>
  );
}

function CalendarCardDay({
  dateObjects,
  day,
  dateNumbersToHighlight,
  outsideCurrentMonth,
  selected,
  ...props
}: PickersDayProps<Date> & {
  dateObjects: CalendarDateObject[]
  setSelected?: Dispatch<SetStateAction<Date>>
  dateNumbersToHighlight: number[]
}) {
  const { t } = useTranslation();
  let thisDayCourses: CalendarDateObject[] = [];
  if (dateObjects.length > 0) {
    thisDayCourses = dateObjects.filter((dateObject) => {
      const courseDate = format(new Date(dateObject.date.join("/")), "yyyy/MM/dd");
      const thisDayDate = format(day, "yyyy/MM/dd");
      const compare = compareAsc(new Date(thisDayDate), new Date(courseDate)) == 0;
      return compare;
    });
  }
  const activityColor = useDayColor(thisDayCourses);

  const currentDay = new Date();

  const language = useLanguageStore(state => state.language)?.id;

  const [locale, setLocale] = useState<Locale>(it);

  useEffect(() => {
    (async () => {
      try {
        const newLocale = await switchLocaleImport(language);
        setLocale(newLocale.default);
      } catch(err) {
        // eslint-disable-next-line no-console
        console.error("import date-fns locale:", err);
      }
    })();
  }, [language]);


  return <StyledPickersDay
    aria-label={ `${format(day, "EEEE, dd MMMM yyyy", { locale })}, ${(selected ? t("selected") : t("not_selected")).toLowerCase()}` }
    outsideCurrentMonth={ outsideCurrentMonth }
    day={ day }
    activityColor={ activityColor }
    $sameDay={ isSameDay(day, currentDay) }
    $selected={ selected ?? false }
    { ...props }
  >
    <span
      style={ {
        height: "1.5625rem",
        lineHeight: "1.5625rem",
        width: "1.5625rem"
      } }
    >
      { format(day, "d", { locale: locale }) }
    </span>
    { /* (
      thisDayCourses.length > 0
        && !outsideCurrentMonth
        && !selected
    ) ? <DotSpan /> : null
    */ }
    {
      (
        dateNumbersToHighlight
        && dateNumbersToHighlight.includes(day.getDate())
        && !outsideCurrentMonth
        && !selected
      ) ? <DotSpan /> : null
    }
  </StyledPickersDay>;
}

/**
 * SeededCalendarDay is a "useless" wrapper, meant to appease typescript and the mui calendar prop types
 * @param CalendarDay props
 * @returns a CalendarDay component, typed with additional mui calendar props
 */
const SeededCalendarCardDay = (props: {
  dateObjects: CalendarDateObject[],
  dateNumbersToHighlight: number[],
  setSelected: Dispatch<SetStateAction<Date>>
}) => withPickersDayProps<{
  dateObjects: CalendarDateObject[]
  dateNumbersToHighlight: number[]
  setSelected: Dispatch<SetStateAction<Date>>
}>({
  component: CalendarCardDay,
  ...props
});
